"use client";

import {
  Proficiency,
  useLearnLanguage,
} from "@/context/learn-language-context";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Button } from "@/components/ui/button";
import { CirclePlus } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { SupportedLanguages } from "@/components/language/supported-languages";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useUserInformation } from "@/hooks/api/useUserProfileApi";

interface UserLearnLanguagesDropdownProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function UserLearnLanguagesDropdown({
  value,
  onValueChange,
  placeholder = "Select a language and proficiency",
  disabled = false,
}: UserLearnLanguagesDropdownProps) {
  const { setLanguageProficiency } = useLearnLanguage();
  const { t, i18n } = useTranslation(["common"]);
  const { data: userInformation, isLoading } = useUserInformation();
  const navigate = useNavigate();

  // Get language display name from the supported languages
  const getLanguageDisplayName = (languageCode: string) => {
    const language = SupportedLanguages.find(
      (lang) => lang.key === languageCode,
    );
    return language ? language.label : languageCode.toUpperCase();
  };

  // Create dropdown options from user's learn languages
  const getDropdownOptions = () => {
    if (!userInformation?.learnLanguages) return [];

    return userInformation.learnLanguages.map((learnLang) => {
      const languageName = getLanguageDisplayName(learnLang.languageCode);
      const displayText = `${languageName}`;
      const optionValue = `${learnLang.languageCode}-${learnLang.proficiency}`;

      return {
        value: optionValue,
        label: displayText,
        languageCode: learnLang.languageCode,
        proficiency: learnLang.proficiency,
        id: learnLang.id,
      };
    });
  };

  const handleValueChange = (selectedValue: string) => {
    const languageCode = selectedValue.split("-")[0];
    const proficiency = selectedValue.split("-")[1] as Proficiency;
    setLanguageProficiency({
      language: SupportedLanguages.find((lang) => lang.key === languageCode)!,
      proficiency: proficiency,
    });

    if (onValueChange) {
      onValueChange(selectedValue);
    }
  };

  const options = getDropdownOptions();

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-center p-2 text-muted-foreground">
          <Spinner className="size-4" />
          {t("common:languageProficiency.loadingUserLanguages")}
        </div>
      </>
    );
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <Select
      value={value}
      onValueChange={handleValueChange}
      disabled={disabled || isLoading}
    >
      <SelectTrigger className="rounded-full border-primary hover:border-2 items-center justify-between ">
        <div /> {/* Empty div to push the value to center */}
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <div>
          {options.map((option) => (
            <SelectItem
              key={option.id}
              value={option.value}
              className="items-center justify-center gap-4"
            >
              <div className="flex flex-row gap-2 sm:gap-4 items-center justify-center">
                <span className="text-primary dark:brightness-125 font-bold">
                  {t("common:languages." + option.languageCode)}
                </span>
                {option.languageCode != i18n.language && (
                  <span className="hidden md:inline text-primary font-light">
                    {option.label}
                  </span>
                )}
                <div className="hidden sm:inline-flex items-center">
                  <div className="w-px h-4 bg-border bg-primary mr-6" />
                  <span className="font-medium text-sm text-foreground">
                    {t("common:proficiencyLevels." + option.proficiency)}
                  </span>
                </div>
              </div>
            </SelectItem>
          ))}
          <Button
            onClick={() => navigate("/app/profile")}
            variant="link"
            className="w-full justify-center"
          >
            <CirclePlus />
          </Button>
        </div>
      </SelectContent>
    </Select>
  );
}

export default UserLearnLanguagesDropdown;
